import React, { useState, useEffect } from 'react';
import { hasPermission } from '../utils/permissions'; // Import the permission utility
import './Invitations.css';

const Invitations = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [societies, setSocieties] = useState([]);
  const [selectedSociety, setSelectedSociety] = useState('');
  const [canAcceptInvitations, setCanAcceptInvitations] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  // Load societies and initial data

  // Fetch invitations when society changes
  useEffect(() => {
    
      fetchInvitations();
    // eslint-disable-next-line
  }, [selectedSociety]);

  // Add useEffect to check permissions
  useEffect(() => {
    const checkPermissions = () => {
      const hasAcceptPermission = hasPermission('org:accept-invitations',localStorage.getItem('societyId'));
      setCanAcceptInvitations(hasAcceptPermission);
    };

    checkPermissions();
  }, [selectedSociety]); // Re-check when society changes

  // eslint-disable-next-line
  const loadSocieties = () => {
    try {
      const profileData = JSON.parse(localStorage.getItem('profileData') || '{}');
      const currentSocietyId = localStorage.getItem('societyId');
      
      if (profileData.Orgs && profileData.Orgs.length > 0) {
        const formattedSocieties = profileData.Orgs.map(org => ({
          id: org.OrgId,
          name: org.OrgName
        }));
        
        setSocieties(formattedSocieties);
        setSelectedSociety(currentSocietyId || formattedSocieties[0].id);
      } else {
        throw new Error('No societies found in profile data');
      }
    } catch (error) {
      console.error('Error loading societies:', error);
      setError('Failed to load societies');
      setLoading(false);
    }
  };

  useEffect(() => {
    // Listen for profile data changes
    const handleProfileUpdate = () => {
      // Remove this line
    };

    window.addEventListener('profileUpdate', handleProfileUpdate);

    return () => {
      window.removeEventListener('profileUpdate', handleProfileUpdate);
    };
  }, []);

  const handleSocietyChange = (e) => {
    const newSocietyId = e.target.value;
    setSelectedSociety(newSocietyId);
    localStorage.setItem('societyId', newSocietyId);
  };

  const fetchInvitations = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const societyId = localStorage.getItem('societyId');

      if (!token || !societyId) {
        throw new Error('Missing required data: token or society ID');
      }

      console.log('Fetching invitations for society:', societyId);

      const url = new URL(`${API_URL}/api/v1/auth/invitations`);
      url.searchParams.append('access_token', token);
      url.searchParams.append('org_id', societyId);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch invitations');
      }

      const data = await response.json();
      console.log('Invitations response:', data);

      if (data.status && data.invitations) {
        setInvitations(data.invitations);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
      setError('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleInvitationResponse = async (emailId, status) => {
    try {
      const token = localStorage.getItem('accessToken');
      const societyId = localStorage.getItem('societyId');
      
      // Construct the URL based on status
      const endpoint = status === 'rejected' ? 'reject' : 'accept';
      const url = new URL(`${API_URL}/api/v1/auth/invitations/${endpoint}`);
      url.searchParams.append('access_token', token);
      url.searchParams.append('org_id', societyId);

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email_id: emailId
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to ${status} invitation`);
      }

      const result = await response.json();
      if (result.status) {
        // Refresh invitations list after successful action
        fetchInvitations();
      } else {
        throw new Error(result.message || `Failed to ${status} invitation`);
      }
    } catch (error) {
      console.error(`Error ${status}ing invitation:`, error);
      setError(`Failed to ${status} invitation: ${error.message}`);
    }
  };

  if (loading) {
    return <div className="invitations-loading">Loading invitations...</div>;
  }

  if (error) {
    return <div className="invitations-error">{error}</div>;
  }

  return (
    <div className="invitations-container">
      <div className="page-header">
        <div className="header-content">
          <h2>Pending Invitations</h2>
          <div className="society-selector">
            <select 
              value={selectedSociety}
              onChange={handleSocietyChange}
              className="society-select"
            >
              {societies.map(society => (
                <option key={society.id} value={society.id}>
                  {society.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {invitations.length === 0 ? (
        <div className="no-invitations">
          <i className="fas fa-envelope-open"></i>
          <p>No pending invitations</p>
        </div>
      ) : (
        <div className="invitations-grid">
          {invitations.map((invitation) => (
            <div key={invitation.EmailID} className="invitation-item">
              <div className="invitation-item-header">
                <div className="invitation-society">
                  <i className="fas fa-building"></i>
                  <span>{invitation.OrgName}</span>
                </div>
                <span className={`invitation-status status-${invitation.InvitationStatus.toLowerCase()}`}>
                  {invitation.InvitationStatus}
                </span>
              </div>

              <div className="invitation-item-content">
                <div className="invitation-detail">
                  <i className="fas fa-user"></i>
                  <span>Name: {`${invitation.FirstName} ${invitation.LastName}`}</span>
                </div>
                <div className="invitation-detail">
                  <i className="fas fa-envelope"></i>
                  <span>Email: {invitation.EmailID}</span>
                </div>
                <div className="invitation-detail">
                  <i className="fas fa-home"></i>
                  <span>Room: {invitation.RoomNo}</span>
                </div>
              </div>

              {canAcceptInvitations && invitation.InvitationStatus.toLowerCase() === 'pending' && (
                <div className="invitation-actions">
                  <button
                    className="action-button accept"
                    onClick={() => handleInvitationResponse(invitation.EmailID, 'accepted')}
                  >
                    <i className="fas fa-check"></i>
                    Accept
                  </button>
                  <button
                    className="action-button reject"
                    onClick={() => handleInvitationResponse(invitation.EmailID, 'rejected')}
                  >
                    <i className="fas fa-times"></i>
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Invitations; 